import { create } from 'zustand';
import type { Book, Chapter, LibrarySource, ProgressFile } from '../types';
import { EMPTY_PROGRESS } from '../types';
import {
  deviceLabel,
  readCachedLibrary,
  readLocalProgress,
  writeCachedLibrary,
  writeLocalProgress,
} from '../lib/localStore';
import {
  isEffectivelyFinished,
  makeEntry,
  mergeProgress,
  shouldPromptResume,
} from '../lib/progress';
import { currentRoute, navigateTo, screenKey, toHash, type Route } from '../lib/router';
import { screenTransition } from '../lib/transition';
import { applyDuration, measureStartedBooks, withKnownDurations } from '../lib/durations';

export type LibraryStatus = 'idle' | 'loading' | 'ready' | 'error';
export type SyncStatus = 'idle' | 'syncing' | 'saved' | 'error';

export interface ResumePrompt {
  bookId: string;
  bookTitle: string;
  trackIndex: number;
  positionSec: number;
  device: string;
}

/** Local progress is written on every record; only the remote push is
 *  throttled, to keep the Drive write rate sane over a long session. */
const REMOTE_THROTTLE_MS = 25_000;

interface AppState {
  source: LibrarySource | null;
  sourceKey: string;

  books: Book[];
  libraryStatus: LibraryStatus;
  libraryError: string | null;

  progress: ProgressFile;
  syncStatus: SyncStatus;
  syncError: string | null;
  resumePrompt: ResumePrompt | null;

  /* --- playback: owned by player/engine.ts, mirrored here for the UI --- */
  currentBookId: string | null;
  chapters: Chapter[];
  trackIndex: number;
  positionSec: number;
  trackDurationSec: number;
  playing: boolean;
  buffering: boolean;
  speed: number;
  playbackError: string | null;
  sleepEndsAt: number | null;
  sleepAtChapterEnd: boolean;

  route: Route;
  /** The library screen last shown, search included, so the player's back
   *  button returns to wherever the book was opened from. */
  lastLibraryRoute: Route | null;

  setSource: (source: LibrarySource, sourceKey: string) => Promise<void>;
  loadLibrary: (opts?: { force?: boolean }) => Promise<void>;
  syncFromRemote: () => Promise<void>;
  /** Measures lengths of started books that have none, so the library can show
   *  how far through them you are without opening each one. */
  measureMissingDurations: () => Promise<void>;
  recordProgress: (bookId: string, trackIndex: number, positionSec: number) => void;
  flushProgress: () => Promise<void>;
  dismissResumePrompt: () => void;
  acceptResumePrompt: () => void;
  setRoute: (route: Route) => void;
  navigate: (route: Route, opts?: { replace?: boolean }) => void;
  patchPlayback: (patch: Partial<AppState>) => void;
}

let remoteTimer: ReturnType<typeof setTimeout> | null = null;
/** The route an animated change is already heading to, so the hashchange that
 *  our own navigation causes does not start a second, competing one. */
let routing: string | null = null;
let measuring: Promise<void> | null = null;

export const useApp = create<AppState>((set, get) => ({
  source: null,
  sourceKey: 'none',

  books: [],
  libraryStatus: 'idle',
  libraryError: null,

  progress: EMPTY_PROGRESS,
  syncStatus: 'idle',
  syncError: null,
  resumePrompt: null,

  currentBookId: null,
  chapters: [],
  trackIndex: 0,
  positionSec: 0,
  trackDurationSec: 0,
  playing: false,
  buffering: false,
  speed: 1,
  playbackError: null,
  sleepEndsAt: null,
  sleepAtChapterEnd: false,

  route: currentRoute(),
  lastLibraryRoute: currentRoute().name === 'library' ? currentRoute() : null,

  async setSource(source, sourceKey) {
    set({ source, sourceKey, books: [], libraryStatus: 'idle', progress: EMPTY_PROGRESS });
    // Local state first so the UI is useful before any network call resolves.
    const local = await readLocalProgress(sourceKey);
    set({ progress: local });
    await get().loadLibrary();
    await get().syncFromRemote();
  },

  async loadLibrary({ force = false } = {}) {
    const { source, sourceKey } = get();
    if (!source) return;

    if (!force) {
      const cached = await readCachedLibrary(sourceKey);
      if (cached && cached.books.length > 0) {
        set({ books: await withKnownDurations(cached.books), libraryStatus: 'ready' });
      } else {
        set({ libraryStatus: 'loading' });
      }
    } else {
      set({ libraryStatus: 'loading' });
    }

    try {
      const books = await withKnownDurations(await source.listBooks());
      set({ books, libraryStatus: 'ready', libraryError: null });
      await writeCachedLibrary(sourceKey, books);
      void get().measureMissingDurations();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // A cached library is better than an error screen when a refresh fails.
      set({
        libraryStatus: get().books.length > 0 ? 'ready' : 'error',
        libraryError: message,
      });
    }
  },

  async syncFromRemote() {
    const { source, progress, currentBookId, books } = get();
    if (!source) return;
    set({ syncStatus: 'syncing', syncError: null });
    try {
      const remote = await source.loadProgress();
      const merged = mergeProgress(progress, remote);
      set({ progress: merged, syncStatus: 'saved', syncError: null });
      await writeLocalProgress(get().sourceKey, merged);
      // Another device may have started books this one has never opened.
      void get().measureMissingDurations();

      // Only interrupt about the book that is actually open.
      if (currentBookId && remote) {
        const incoming = remote.books[currentBookId];
        const mine = progress.books[currentBookId];
        if (shouldPromptResume(incoming, mine) && incoming) {
          const book = books.find((b) => b.id === currentBookId);
          set({
            resumePrompt: {
              bookId: currentBookId,
              bookTitle: book?.title ?? 'this book',
              trackIndex: incoming.trackIndex,
              positionSec: incoming.positionSec,
              device: incoming.device,
            },
          });
        }
      }
    } catch (err) {
      set({
        syncStatus: 'error',
        syncError: err instanceof Error ? err.message : String(err),
      });
    }
  },

  recordProgress(bookId, trackIndex, positionSec) {
    const { progress, books, trackDurationSec } = get();
    const book = books.find((b) => b.id === bookId);
    const trackCount = book?.tracks.length ?? 1;

    const totalKnown = book
      ? book.tracks.reduce((sum, t) => sum + (t.durationSec ?? 0), 0)
      : 0;

    const entry = makeEntry(
      trackIndex,
      positionSec,
      totalKnown,
      deviceLabel(),
      isEffectivelyFinished(trackIndex, trackCount, positionSec, trackDurationSec),
    );

    const next: ProgressFile = {
      version: 1,
      books: { ...progress.books, [bookId]: entry },
    };
    set({ progress: next });

    // Local storage is written straight away rather than debounced: an
    // IndexedDB put every couple of seconds costs nothing, and any delay here
    // is a window in which a closed tab loses your place. The remote push is
    // the one that needs throttling.
    void writeLocalProgress(get().sourceKey, next);

    if (remoteTimer === null) {
      remoteTimer = setTimeout(() => {
        remoteTimer = null;
        void pushRemote(get, set);
      }, REMOTE_THROTTLE_MS);
    }
  },

  measureMissingDurations() {
    measuring ??= (async () => {
      const { source, books, progress } = get();
      if (!source) return;
      await measureStartedBooks(books, progress, source, (trackId, seconds) => {
        set({ books: applyDuration(get().books, trackId, seconds) });
      });
    })().finally(() => {
      measuring = null;
    });
    return measuring;
  },

  async flushProgress() {
    if (remoteTimer !== null) {
      clearTimeout(remoteTimer);
      remoteTimer = null;
    }
    await writeLocalProgress(get().sourceKey, get().progress);
    await pushRemote(get, set);
  },

  dismissResumePrompt() {
    set({ resumePrompt: null });
  },

  /** Consumed by the player, which owns the actual seek. */
  acceptResumePrompt() {
    set({ resumePrompt: null });
  },

  setRoute(route) {
    const prev = get().route;
    const hash = toHash(route);
    if (hash === toHash(prev) || hash === routing) return;

    const apply = () => {
      routing = null;
      set(route.name === 'library' ? { route, lastLibraryRoute: route } : { route });
    };
    // Typing a search changes the URL, not the screen: no animation for that.
    if (screenKey(prev) === screenKey(route)) return apply();

    routing = hash;
    // The cover that flies: the book being opened, or the one being left.
    const bookId =
      route.name === 'player' ? route.bookId
      : prev.name === 'player' ? prev.bookId
      : null;
    screenTransition(apply, bookId);
  },

  navigate(route, opts) {
    navigateTo(route, opts);
    get().setRoute(route);
  },

  patchPlayback(patch) {
    set(patch as Partial<AppState>);
  },
}));

async function pushRemote(
  get: () => AppState,
  set: (patch: Partial<AppState>) => void,
): Promise<void> {
  const { source, progress } = get();
  if (!source) return;
  set({ syncStatus: 'syncing' });
  try {
    await source.saveProgress(progress);
    set({ syncStatus: 'saved', syncError: null });
  } catch (err) {
    set({ syncStatus: 'error', syncError: err instanceof Error ? err.message : String(err) });
  }
}

/* ---------- selectors ---------- */

export function selectCurrentBook(state: AppState): Book | null {
  if (!state.currentBookId) return null;
  return state.books.find((b) => b.id === state.currentBookId) ?? null;
}

export function bookById(state: AppState, id: string | null): Book | null {
  if (!id) return null;
  return state.books.find((b) => b.id === id) ?? null;
}
